import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useSnackbar } from 'notistack';
import {
  TrendingUp as TradingIcon,
  History as HistoryIcon,
  AccountBalance as BalanceIcon,
} from '@mui/icons-material';

import OrderForm from '../components/trading/orders/OrderForm';
import tradingService, { MarketData, Balance, Order } from '../services/tradingService';
import btraderService from '../services/btraderService';
import { useSimulation } from '../contexts/SimulationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trading-tabpanel-${index}`}
      aria-labelledby={`trading-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const TradingPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { state: simulationState, isLoading: simulationLoading, refreshState } = useSimulation();
  const isSimulationActive = Boolean(simulationState?.is_enabled);
  const virtualBalance = simulationState?.virtual_balance ?? 0;

  const [currentTab, setCurrentTab] = useState(0);
  const [isApiConnected, setIsApiConnected] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<MarketData | null>(null);
  const [markets, setMarkets] = useState<MarketData[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);

  const loadMarketTicker = useCallback(
    async (market: string) => {
      try {
        const tickerData = await tradingService.getMarketTicker(market);

        setSelectedMarket(prev => {
          if (prev && prev.symbol === market && tickerData.ticker) {
            return {
              ...prev,
              price: parseFloat(tickerData.ticker.lastPrice || '0'),
              change24h: parseFloat(tickerData.ticker.priceChangePercent || '0'),
              volume24h: parseFloat(tickerData.ticker.volume || '0'),
            };
          }
          return prev;
        });
      } catch (err) {
        console.error('Failed to load ticker data:', err);
      }
    },
    [],
  );

  const loadMarkets = useCallback(async () => {
    try {
      const marketData = await tradingService.getMarkets();
      setMarkets(marketData);

      const defaultMarket = marketData.find(m => m.symbol === 'BTC-EUR') || marketData[0];
      if (defaultMarket) {
        setSelectedMarket(defaultMarket);
        await loadMarketTicker(defaultMarket.symbol);
      }
    } catch (err) {
      console.error('Failed to load markets:', err);
    }
  }, [loadMarketTicker]);

  const loadBalancesFromApi = useCallback(async () => {
    try {
      const balanceData = await tradingService.getBalances();
      setBalances(balanceData);
    } catch (err) {
      console.error('Failed to load balances:', err);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const orderData = await tradingService.getOrders();
      setOrders(orderData);
    } catch (err) {
      console.error('Failed to load orders:', err);
    }
  }, []);

  const initializeTradingData = useCallback(async () => {
    setIsPageLoading(true);
    setError(null);

    try {
      if (isSimulationActive) {
        setIsApiConnected(true);
        await Promise.all([loadMarkets(), loadOrders()]);
      } else {
        const connected = await btraderService.checkConnection();
        setIsApiConnected(connected);

        if (connected) {
          await Promise.all([loadMarkets(), loadBalancesFromApi(), loadOrders()]);
        }
      }
    } catch (err) {
      setError('Fehler beim Laden der Trading-Daten');
      console.error('Trading data initialization error:', err);
    } finally {
      setIsPageLoading(false);
    }
  }, [isSimulationActive, loadBalancesFromApi, loadMarkets, loadOrders]);

  useEffect(() => {
    void initializeTradingData();
  }, [initializeTradingData]);

  useEffect(() => {
    if (isSimulationActive) {
      setBalances([
        {
          symbol: 'EUR (Sim)',
          available: virtualBalance,
          inOrder: 0,
          total: virtualBalance,
        },
      ]);
    }
  }, [isSimulationActive, virtualBalance]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleMarketChange = async (market: MarketData) => {
    setSelectedMarket(market);
    await loadMarketTicker(market.symbol);
  };

  const handleOrderSubmit = async (orderData: { side: 'buy' | 'sell'; type: 'market' | 'limit' | 'stop-limit'; quantity: number; price?: number; stopPrice?: number; timeInForce?: 'GTC' | 'IOC' | 'FOK' }) => {
    if (!selectedMarket) {
      return;
    }

    try {
      setIsPageLoading(true);
      await tradingService.createOrder({
        market: selectedMarket.symbol,
        side: orderData.side,
        order_type: orderData.type,
        amount: orderData.quantity,
        price: orderData.price,
        time_in_force: orderData.timeInForce,
      });

      if (isSimulationActive) {
        await loadOrders();
        await refreshState();
      } else {
        await Promise.all([loadOrders(), loadBalancesFromApi()]);
      }

      enqueueSnackbar('Order erfolgreich erstellt!', { variant: 'success' });
    } catch (err) {
      const errorMessage = tradingService.formatError(err);
      enqueueSnackbar(`Fehler beim Erstellen der Order: ${errorMessage}`, { variant: 'error' });
    } finally {
      setIsPageLoading(false);
    }
  };

  const findBalanceAmount = useCallback(
    (symbol: string) => {
      const direct = balances.find(b => b.symbol === symbol);
      if (direct) {
        return direct.available;
      }
      const fallback = balances.find(b => b.symbol.startsWith(symbol));
      return fallback ? fallback.available : 0;
    },
    [balances],
  );

  const currentBalance = useMemo(() => {
    if (!selectedMarket) {
      return { base: 0, quote: 0 };
    }
    const [baseSymbol, quoteSymbol] = selectedMarket.symbol.split('-');
    return {
      base: findBalanceAmount(baseSymbol),
      quote: findBalanceAmount(quoteSymbol),
    };
  }, [findBalanceAmount, selectedMarket]);

  const simulationBalanceLabel = useMemo(
    () =>
      virtualBalance.toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }),
    [virtualBalance],
  );

  if (isPageLoading || simulationLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!isApiConnected && !isSimulationActive) {
    return (
      <>
        <Helmet>
          <title>Trading - btrader</title>
        </Helmet>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Trading
          </Typography>
          <Alert severity="warning">
            <Typography variant="h6" gutterBottom>
              BTRADER API nicht verbunden
            </Typography>
            <Typography variant="body2">
              Um mit dem Trading zu beginnen, müssen Sie zuerst Ihre BTRADER API-Keys in den
              Einstellungen konfigurieren.
            </Typography>
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Trading - btrader</title>
      </Helmet>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Trading
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isSimulationActive && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Simulationsmodus aktiv – Orders werden ohne echte BTRADER-API-Keys verarbeitet. Virtuelles Guthaben:{' '}
            <strong>{simulationBalanceLabel}</strong>
          </Alert>
        )}

        <Paper sx={{ width: '100%', mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="trading tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<TradingIcon />}
              label="Spot Trading"
              id="trading-tab-0"
              aria-controls="trading-tabpanel-0"
            />
            <Tab
              icon={<HistoryIcon />}
              label="Order-Historie"
              id="trading-tab-1"
              aria-controls="trading-tabpanel-1"
            />
            <Tab
              icon={<BalanceIcon />}
              label="Guthaben"
              id="trading-tab-2"
              aria-controls="trading-tabpanel-2"
            />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {/* Spot Trading Tab */}
            <TabPanel value={currentTab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {selectedMarket && (
                    <OrderForm
                      market={selectedMarket}
                      balance={currentBalance}
                      onSubmitOrder={handleOrderSubmit}
                      loading={isPageLoading}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  {/* Market Selector */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Trading-Paar auswählen
                    </Typography>
                    <Grid container spacing={1}>
                      {tradingService.getPopularMarkets().map((market) => {
                        const marketData = markets.find(m => m.symbol === market);
                        return (
                          <Grid item key={market}>
                            <Chip
                              label={tradingService.formatMarketSymbol(market).formatted}
                              onClick={() => marketData && handleMarketChange(marketData)}
                              color={selectedMarket?.symbol === market ? 'primary' : 'default'}
                              variant={selectedMarket?.symbol === market ? 'filled' : 'outlined'}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Paper>

                  {/* Market Info */}
                  {selectedMarket && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {tradingService.formatMarketSymbol(selectedMarket.symbol).formatted}
                      </Typography>
                      <Typography variant="h4" fontWeight={600}>
                        {tradingService.formatPrice(selectedMarket.price)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={selectedMarket.change24h >= 0 ? 'success.main' : 'error.main'}
                      >
                        {selectedMarket.change24h >= 0 ? '+' : ''}{selectedMarket.change24h.toFixed(2)}%
                      </Typography>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            </TabPanel>

            {/* Order History Tab */}
            <TabPanel value={currentTab} index={1}>
              <Typography variant="h6" gutterBottom>
                Order-Historie
              </Typography>
              {orders.length > 0 ? (
                <Grid container spacing={2}>
                  {orders.map((order) => (
                    <Grid item xs={12} key={order.id}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {tradingService.formatMarketSymbol(order.market).formatted}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {order.side.toUpperCase()} • {order.order_type} • {order.amount}
                            </Typography>
                          </Box>
                          <Chip
                            label={tradingService.getOrderStatusLabel(order.status)}
                            color={tradingService.getOrderStatusColor(order.status)}
                            size="small"
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">
                  Noch keine Trading-Orders vorhanden.
                </Alert>
              )}
            </TabPanel>

            {/* Balances Tab */}
            <TabPanel value={currentTab} index={2}>
              <Typography variant="h6" gutterBottom>
                Kontoguthaben
              </Typography>
              {balances.length > 0 ? (
                <Grid container spacing={2}>
                  {balances.map((balance) => (
                    <Grid item xs={12} sm={6} md={4} key={balance.symbol}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {balance.symbol}
                        </Typography>
                        <Typography variant="h6">
                          {tradingService.formatAmount(balance.total, balance.symbol)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Verfügbar: {tradingService.formatAmount(balance.available, balance.symbol)}
                        </Typography>
                        {balance.inOrder > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            In Order: {tradingService.formatAmount(balance.inOrder, balance.symbol)}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">
                  Keine Guthaben mit positivem Saldo gefunden.
                </Alert>
              )}
            </TabPanel>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default TradingPage;